import React from 'react';
import { CircleCheck } from 'lucide-react';
import { MRT_ColumnDef } from '@talentmesh/table';
import UIStrings from '@/Utils/UIStrings';
import { RecruiterRoles } from '@/Models/RecruiterRoles';
import RoleColumnCell from '../TeamMembersTable/Components/RoleColumnCell';
import AllowanceColumnHeader from '../TeamMembersTable/Components/AllowanceColumnHeader';
import Header from '../TeamMembersTable/Components/Header';

export interface RoleItemProps {
    name: RecruiterRoles;
    description: string;
    inviteMember: boolean;
    createRecruitment: boolean;
    useAts: boolean;
    viewApplicants: boolean;
}

function useRoleColumn(): MRT_ColumnDef<RoleItemProps> {
    return {
        id: 'name',
        accessorKey: 'name',
        header: UIStrings.Role,
        Header: () => <Header header={UIStrings.Role} />,
        size: 408,
        enableSorting: false,
        mantineTableBodyCellProps: { className: 'border-border border-y-0 border-r' },
        mantineTableHeadCellProps: { className: 'border-border border-y-0 border-r' },
        Cell: (value) => <RoleColumnCell value={value.row.original} />,
    };
}

function useAllowanceColumn(fieldName: keyof RoleItemProps, headerName: string): MRT_ColumnDef<RoleItemProps> {
    return {
        id: String(fieldName),
        accessorKey: fieldName,
        header: headerName,
        Header: () => <AllowanceColumnHeader headerName={headerName} />,
        size: 100,
        minSize: 72,
        maxSize: 140,
        enableSorting: false,
        mantineTableBodyCellProps: {
            className: 'max-w-24 min-w-0 text-center',
        },
        mantineTableHeadCellProps: {
            className:
                'max-w-24 min-w-0 whitespace-normal py-2 px-2 text-center align-top [&_.mrt-table-head-cell-content]:min-h-0 [&_.mrt-table-head-cell-content]:w-full [&_.mrt-table-head-cell-content]:min-w-0 [&_.mrt-table-head-cell-content]:mrt-table-head-cell-content-col [&_.mrt-table-head-cell-content]:mrt-table-head-cell-content-nowrap [&_.mrt-table-head-cell-content]:items-center [&_.mrt-table-head-cell-content]:justify-center [&_.mrt-table-head-cell-content]:gap-1 [&_.mrt-table-head-cell-content]:whitespace-normal',
        },
        Cell: (value) =>
            value ? (
                <CircleCheck className="mx-auto size-5 shrink-0 text-green-600 dark:text-green-500" aria-hidden />
            ) : null,
    };
}

function useColumns(): MRT_ColumnDef<RoleItemProps>[] {
    return [
        useRoleColumn(),
        useAllowanceColumn('inviteMember', UIStrings.InviteMembers),
        useAllowanceColumn('createRecruitment', UIStrings.CreateRecruitments),
        useAllowanceColumn('useAts', UIStrings.UseATSFunctionality),
        useAllowanceColumn('viewApplicants', UIStrings.ViewApplicants),
    ];
}

export { useRoleColumn, useAllowanceColumn, useColumns };
